import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../core/api/api-error';
import { signOut } from '../core/api/auth-service';
import { getFreeSlots, submitAppointment } from '../core/api/booking-service';
import { bookPortalAppointment, getPortalProfile } from '../core/api/portal-service';
import { PERSONAL_DATA_CONSENT_VERSION } from '../core/models/personal-data-consent';
import { describeApiError } from '../core/errors/describe-api-error';
import { describeFieldErrors } from '../core/errors/describe-field-errors';
import { useConsultationTypes } from '../core/hooks/use-consultation-types';
import { useLanguage } from '../core/hooks/use-language';
import { useSpecialists } from '../core/hooks/use-specialists';
import { usePageMetadata } from '../core/hooks/use-page-metadata';
import { useRootClass } from '../core/hooks/use-root-class';
import { useSession } from '../core/hooks/use-session';
import { STATIC_PAGES, staticPageMetadata } from '../core/seo/static-pages';
import { usePublicSettings } from '../core/hooks/use-public-settings';
import { useTranslation } from '../core/hooks/use-translation';
import type { TranslationKey } from '../core/i18n/translation';
import { BOOKING_CITIES } from '../core/models/booking-cities';
import type { MeetingFormat } from '../core/models/meeting-format';
import type { PortalProfile } from '../core/models/portal-profile';
import type { PublicConsultationType } from '../core/models/public-consultation-type';
import type { PublicDayAvailability } from '../core/models/public-day-availability';
import { isCompleteSubscriber, toE164, toSubscriberDigits } from '../core/phone/kazakhstan-phone';
import { SettingKeys } from '../core/models/setting-keys';
import { BookingFooter } from './booking/booking-footer';
import { BookingHeader } from './booking/booking-header';
import { AlertIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon } from './booking/booking-icons';
import { BookingSideInfo } from './booking/booking-side-info';
import { BookingStepper } from './booking/booking-stepper';
import { BookingSuccess } from './booking/booking-success';
import { ContactStep, type ContactField } from './booking/contact-step';
import { FIELD_STEP } from './booking/field-step';
import { pageCount, pageOf } from './booking/week-grid';
import { ServiceStep } from './booking/service-step';
import { SlotStep } from './booking/slot-step';
import { SpecialistStep } from './booking/specialist-step';
import { useRevealOnPhone } from './booking/use-reveal-on-phone';

const DEFAULT_HORIZON_DAYS = 62;

/** The calendar, which an online request skips, and the last step, which nothing skips. */
const SLOT_STEP = 3;
const CONTACT_STEP = 4;

/**
 * The public booking wizard: service → consultant → day and time → contact details, with the
 * calendar struck out for an online meeting. The chrome, the stepper, the cards and the calendar
 * are the live `book.html` port; what changed underneath is that half of what the browser used to
 * decide is now the server's.
 *
 * **The consultant is asked before the calendar, and may be skipped.** A visitor who clicked a face
 * on the home page arrives with that person already picked, from `?specialist=`; a visitor who came
 * from the navigation is offered "whoever is free" first and selected by default. The choice is not
 * decoration: the calendar afterwards draws that consultant's own diary, and the request arrives
 * assigned to them — skipping leaves it in the staff queue, which is where an administrator, or the
 * member who takes it on, assigns somebody.
 *
 * Four rules this screen exists to keep:
 *
 * - **The browser never computes an instant, or a date.** The picked slot travels as the
 *   `"YYYY-MM-DD"` and `"HH:mm"` the slots route returned, and the window's start is left to the
 *   server. `new Date('2026-09-06T14:30')` reads the *device's* zone, so a customer on Moscow time
 *   would hold a slot an hour from the one they clicked — and the server would accept it, because
 *   it is a perfectly valid instant.
 * - **The calendar shows the company's days, not the device's.** The live grid greys out Sunday
 *   with `getDay() === 0` and stops thirty days after `new Date()`; here the month grid is drawn
 *   from the availability window and its arrows stop where that window does.
 * - **A lost race costs the customer nothing but the slot.** `SlotNoLongerFree` re-reads the day,
 *   strikes the time out and keeps every other answer they have given. Returning to step 1 would
 *   make them pay for a race they did not cause.
 * - **A validation error goes back to the step that owns the field.** The submission happens on
 *   step 3, but the server can name a field on step 1 — marking a control nobody can see shows the
 *   customer nothing at all.
 *
 * **A signed-in customer books as themselves.** The last step shows their account's name, phone and
 * email read-only and the request goes to the portal route, which takes those details from the
 * account and files the appointment under it — so it appears in the portal, where they can cancel
 * it. Booking for somebody else is signing out, which returns the ordinary form.
 *
 * The step and the selections live in the query string, so the back button works and a
 * half-finished booking survives a reload. That only became safe once the language switch stopped
 * dropping the query string on a language change.
 */
export function BookingPage() {
    const { t, language } = useTranslation();
    const activeLanguage = useLanguage();

    // The page had no head of its own, which mattered less while every page shared one. Now that
    // the build writes this one's tags into its served HTML, the component has to describe the same
    // page — so it reads the same entry the build did.
    usePageMetadata(staticPageMetadata(STATIC_PAGES[2], activeLanguage));

    // `book.html` is drawn at a 15px root that steps up at three breakpoints, and every length in
    // its stylesheet is a `rem`. See `useRootClass`.
    useRootClass('book-root');

    const { settings } = usePublicSettings();
    // Both lists arrive with the page rather than after it — see `site-data-store`.
    const { specialists, loading: specialistsLoading, settled: specialistsSettled } = useSpecialists();
    const { types, loading: typesLoading } = useConsultationTypes();
    const [searchParams, setSearchParams] = useSearchParams();

    const [calendar, setCalendar] = useState<PublicDayAvailability[]>([]);
    const [calendarLoading, setCalendarLoading] = useState(false);

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');

    const session = useSession();
    const signedIn = session !== null;
    // Undefined while it is being read, null when the read failed.
    const [profile, setProfile] = useState<PortalProfile | null | undefined>(undefined);

    // Deliberately not in the URL, unlike the wizard's selections: consent has to be an act on this
    // page, and a link that arrives pre-consented is not one.
    const [consent, setConsent] = useState(false);

    // Null until the customer touches either control, so both follow what they have already chosen:
    // the tab opens on the band the picked service is in, and the calendar opens on the page of the
    // picked day. Neither can be derived once it has been overridden, hence the null.
    const [paidTab, setPaidTab] = useState<boolean | null>(null);
    const [shownPage, setShownPage] = useState<number | null>(null);

    const [takenTimes, setTakenTimes] = useState<string[]>([]);
    const [errorKey, setErrorKey] = useState<TranslationKey | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, TranslationKey>>({});
    const [submitting, setSubmitting] = useState(false);

    // `submitting` only changes on the next render, so a second click or Enter before it would
    // send the request twice — and the twin loses the slot to the first and answers 409.
    const inFlight = useRef(false);
    const [submitted, setSubmitted] = useState<{ slotHeld: boolean } | null>(null);

    // The selections that are worth surviving a reload live in the URL; the contact details
    // deliberately do not — a name and a phone number have no business in a shareable link.
    //
    // Clamped, because the step comes from a typed or shared URL: `?step=9` would render no step
    // at all and `?step=abc` is NaN, and a customer arriving at a blank wizard has no way back.
    const step = clampStep(searchParams.get('step'));
    const consultationTypeId = searchParams.get('service');
    const specialistId = searchParams.get('specialist');
    const city = searchParams.get('city');
    const date = searchParams.get('date');
    const time = searchParams.get('time');

    const selectedType = types.find((type) => type.id === consultationTypeId) ?? null;

    // A free consultation is held in an office only; the server refuses it online.
    const onlineOffered = selectedType === null || isPaid(selectedType);

    // In the URL with the rest: held in state, a reload on the last step silently turned an online
    // request back into an office visit with no time, which the server then refused.
    const format: MeetingFormat = onlineOffered && searchParams.get('format') === 'online' ? 'Online' : 'InPerson';

    // An office meeting offers only the consultants in the chosen office. An online one — or an
    // office one whose city is not chosen yet — offers everybody.
    const office = format === 'Online' ? null : (BOOKING_CITIES.find((entry) => entry.key === city)?.office ?? null);
    const offeredSpecialists = useMemo(
        () => (office === null ? specialists : specialists.filter((specialist) => specialist.city === office)),
        [specialists, office]
    );
    const selectedSpecialist = offeredSpecialists.find((specialist) => specialist.id === specialistId) ?? null;
    const paid = paidTab ?? (selectedType === null ? false : isPaid(selectedType));
    const shownTypes = types.filter((type) => isPaid(type) === paid);

    // The string "false" is truthy, so the obvious `settings.values[key] ?? false` would leave the form
    // open exactly when it had been switched off. The endpoint re-checks regardless: this guard is
    // for the customer who kept the page open, not a rule of its own.
    const bookingOpen = (settings.values[SettingKeys.publicSiteAppointmentsEnabled] ?? 'true') === 'true';

    const horizonDays = Number(settings.values[SettingKeys.appointmentsBookingHorizonDays] ?? DEFAULT_HORIZON_DAYS);

    // The window's first day *is* the server's today — it starts the range at its own date, in
    // `Appointments.TimeZone`, precisely so the browser never has to say what today is.
    const days = useMemo(() => new Map(calendar.map((day) => [day.date, day])), [calendar]);
    const today = calendar.length === 0 ? null : calendar[0].date;
    const pages = today === null ? 1 : pageCount(today, calendar[calendar.length - 1].date);
    const page = shownPage ?? (today === null || date === null ? 0 : Math.min(pageOf(today, date), pages - 1));

    const setParams = useCallback(
        (changes: Record<string, string | null>) => {
            setSearchParams(
                (current) => {
                    const next = new URLSearchParams(current);

                    for (const [key, value] of Object.entries(changes)) {
                        if (value === null) {
                            next.delete(key);
                        } else {
                            next.set(key, value);
                        }
                    }

                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const reveal = useRevealOnPhone(searchParams);

    // A visitor who clicked a face on the home page arrives with that person picked and no city.
    // The office is the one that person works in, so it is filled in rather than asked again.
    const linkedSpecialist = specialists.find((specialist) => specialist.id === specialistId) ?? null;

    useEffect(() => {
        if (format === 'Online' || city !== null || linkedSpecialist === null) {
            return;
        }

        const office = BOOKING_CITIES.find((entry) => entry.office === linkedSpecialist.city);

        if (office !== undefined) {
            setParams({ city: office.key });
        }
    }, [format, city, linkedSpecialist, setParams]);

    // A link is shareable and a card can be switched off, so `?specialist=` may name somebody the
    // site no longer offers. Dropped once the list is current rather than carried to the server:
    // the submission would be refused over a choice the customer can no longer see, and "assign
    // one" is the answer they would have given.
    useEffect(() => {
        // Not on the build's copy: a consultant published since the build is not in it yet.
        if (!specialistsSettled || specialistId === null || selectedSpecialist !== null) {
            return;
        }

        setParams({ specialist: null });
    }, [specialistsSettled, specialistId, selectedSpecialist, setParams]);

    // For display only: the portal route reads the same account on the server, so a failed read
    // here costs the customer the preview, not the booking.
    useEffect(() => {
        setProfile(undefined);

        if (!signedIn) {
            return;
        }

        const controller = new AbortController();

        getPortalProfile(controller.signal)
            .then(setProfile)
            .catch(() => {
                if (!controller.signal.aborted) setProfile(null);
            });

        return () => controller.abort();
    }, [signedIn]);

    const loadCalendar = useCallback(
        (typeId: string, memberId: string | null, signal?: AbortSignal) => {
            setCalendarLoading(true);

            return getFreeSlots(
                typeId,
                Number.isInteger(horizonDays) ? horizonDays : DEFAULT_HORIZON_DAYS,
                memberId,
                signal
            )
                .then(setCalendar)
                .catch(() => setCalendar([]))
                .finally(() => {
                    if (signal?.aborted !== true) {
                        setCalendarLoading(false);
                    }
                });
        },
        [horizonDays]
    );

    // Re-read on entering the step, not once for the life of the page: a slot taken while the
    // customer was typing their name must not still show as free.
    useEffect(() => {
        if (consultationTypeId === null || format === 'Online' || step !== SLOT_STEP) {
            return;
        }

        const controller = new AbortController();

        void loadCalendar(consultationTypeId, selectedSpecialist?.id ?? null, controller.signal);

        return () => controller.abort();
    }, [consultationTypeId, selectedSpecialist, format, step, loadCalendar]);

    function selectService(typeId: string | null): void {
        // A different service re-grids the calendar, so the picked day cannot survive the change.
        const next = types.find((type) => type.id === typeId);
        const changes: Record<string, string | null> =
            next !== undefined && !isPaid(next)
                ? { service: typeId, date: null, time: null, format: null }
                : { service: typeId, date: null, time: null };

        setParams(changes);

        if (typeId !== null) {
            const nextFormat = changes['format'] === null ? 'InPerson' : format;

            reveal(nextFormat === 'InPerson' && city === null ? 'city' : 'actions', changes);
        }

        setTakenTimes([]);
        setShownPage(null);
        setFieldErrors({});
    }

    /**
     * A different consultant is a different diary, so the picked day cannot survive the change —
     * the same rule a different service follows, and for the same reason.
     */
    function selectSpecialist(memberId: string | null): void {
        const changes = { specialist: memberId, date: null, time: null };

        setParams(changes);
        reveal('actions', changes);
        setTakenTimes([]);
        setShownPage(null);
        setFieldErrors({});
    }

    function changeTab(nextPaid: boolean): void {
        setPaidTab(nextPaid);

        // The chosen service belongs to the tab it was chosen in; the other tab does not offer it.
        selectService(null);
    }

    function changeFormat(next: MeetingFormat): void {
        // An online request picks no time and names no city, and the server rejects one that
        // carries either — so they are cleared rather than merely ignored.
        const changes: Record<string, string | null> =
            next === 'Online' ? { format: 'online', city: null, date: null, time: null } : { format: null };

        setParams(changes);
        reveal(next === 'InPerson' && city === null ? 'city' : 'actions', changes);
    }

    /**
     * A different office offers different consultants. One picked in the old office is dropped with
     * the day, which was a time in their diary.
     */
    function selectCity(next: string | null): void {
        const nextOffice = BOOKING_CITIES.find((entry) => entry.key === next)?.office ?? null;
        const keepsSpecialist = selectedSpecialist !== null && selectedSpecialist.city === nextOffice;

        const changes: Record<string, string | null> = keepsSpecialist
            ? { city: next }
            : { city: next, specialist: null, date: null, time: null };

        setParams(changes);

        if (next !== null) {
            reveal('actions', changes);
        }

        setTakenTimes([]);
        setShownPage(null);
        setFieldErrors(({ city: _removed, ...rest }) => rest);
    }

    function goToStep(next: number): void {
        setParams({ step: String(next) });
        reveal('step', { step: String(next) });
        setErrorKey(null);
    }

    function nextFromService(): void {
        const missing: Record<string, TranslationKey> = {};

        if (consultationTypeId === null) {
            missing['consultationTypeId'] = 'validation.required';
        }

        if (format === 'InPerson' && city === null) {
            missing['city'] = 'validation.required';
        }

        if (Object.keys(missing).length > 0) {
            setFieldErrors(missing);
            return;
        }

        setFieldErrors({});
        goToStep(2);
    }

    function nextFromSpecialist(): void {
        // Nothing to check: skipping is an answer, and the server re-reads whichever identifier is
        // in the URL against the list it publishes.
        setFieldErrors({});
        goToStep(format === 'Online' ? CONTACT_STEP : SLOT_STEP);
    }

    function nextFromSlot(): void {
        const missing: Record<string, TranslationKey> = {};

        if (date === null) {
            missing['preferredDate'] = 'validation.required';
        }

        if (time === null) {
            missing['preferredTime'] = 'validation.required';
        }

        setFieldErrors(missing);

        if (Object.keys(missing).length === 0) {
            goToStep(CONTACT_STEP);
        }
    }

    async function onSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();

        if (step < CONTACT_STEP || inFlight.current || consultationTypeId === null) {
            return;
        }

        setErrorKey(null);
        setFieldErrors({});

        // Checked here rather than by disabling the button: a dead control explains nothing, and
        // consent is the one field on the form whose absence a customer will not guess at. The
        // number is checked in the same pass so a short one is corrected in its own box rather than
        // returning as a rejected form — the server normalises and decides either way.
        const problems: Record<string, TranslationKey> = {};

        if (!signedIn && !isCompleteSubscriber(phone)) {
            problems['customerPhone'] = 'validation.phone';
        }

        if (!consent) {
            problems['consentVersion'] = 'booking.consent_required';
        }

        if (Object.keys(problems).length > 0) {
            setFieldErrors(problems);
            return;
        }

        inFlight.current = true;
        setSubmitting(true);

        const online = format === 'Online';

        const booking = {
            consultationTypeId,
            meetingFormat: format,
            preferredLanguage: activeLanguage,
            consentVersion: PERSONAL_DATA_CONSENT_VERSION,
            requestedMemberId: selectedSpecialist?.id ?? null,
            city: online ? null : city,
            preferredDate: online ? null : date,
            preferredTime: online ? null : time
        };

        try {
            const result = signedIn
                ? await bookPortalAppointment(booking)
                : await submitAppointment({
                      ...booking,
                      customerName: firstName.trim(),
                      customerLastName: blankToNull(lastName),
                      customerPhone: toE164(phone),
                      customerEmail: blankToNull(email),
                      company
                  });

            setSubmitted({ slotHeld: result.slotHeld });
        } catch (error) {
            handleSubmitFailure(error);
        } finally {
            inFlight.current = false;
            setSubmitting(false);
        }
    }

    function handleSubmitFailure(error: unknown): void {
        const fields = describeFieldErrors(error);

        if (Object.keys(fields).length > 0) {
            setFieldErrors(fields);

            // Back to the step that owns the first field the server named, so the mark is visible.
            const owningStep = Object.keys(fields)
                .map((field) => FIELD_STEP[field])
                .filter((value): value is number => value !== undefined)
                .sort((left, right) => left - right)[0];

            if (owningStep !== undefined && owningStep !== step) {
                goToStep(owningStep);
            }

            return;
        }

        // The write has its own rate limit — 5 per 10 minutes, against 60 a minute for the
        // calendar reads — so a 429 here means "too many requests sent", not "you browsed too
        // much", and it needs a message of its own. `describeApiError` is keyed on the status and
        // cannot tell the two budgets apart.
        setErrorKey(
            error instanceof ApiError && error.status === 429 ? 'booking.too_many_submissions' : describeApiError(error)
        );

        // Lost the race. Mark the time gone, re-read that day and keep every other answer —
        // the customer did not cause the race and should not pay for it with the whole form.
        if (error instanceof ApiError && error.status === 409 && time !== null && consultationTypeId !== null) {
            setTakenTimes((current) => [...current, time]);
            setParams({ step: String(SLOT_STEP), time: null });
            void loadCalendar(consultationTypeId, selectedSpecialist?.id ?? null);
        }
    }

    const serviceName = selectedType === null ? '' : language === 'ru' ? selectedType.nameRu : selectedType.nameKk;
    const specialistName = selectedSpecialist?.displayName ?? null;
    const online = format === 'Online';

    /**
     * The banner and the two buttons, which every step renders at the foot of **its own card** —
     * that is where the live page puts them, and a button row floating under the card reads as
     * belonging to the page rather than to the step it advances.
     */
    const actions = (
        <>
            {errorKey !== null && (
                <div className="api-err" role="alert">
                    <AlertIcon />
                    <div className="api-err-body">
                        <p className="api-err-title">{t('booking.err.title')}</p>
                        <p className="api-err-detail">{t(errorKey)}</p>
                    </div>
                </div>
            )}

            <div className="btn-row" data-reveal="actions">
                {step > 1 ? (
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => goToStep(step === CONTACT_STEP && online ? 2 : step - 1)}
                    >
                        <ChevronLeftIcon />
                        <span>{t('booking.back')}</span>
                    </button>
                ) : (
                    <span />
                )}

                {step < CONTACT_STEP ? (
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={step === 1 ? consultationTypeId === null : step === SLOT_STEP && time === null}
                        onClick={step === 1 ? nextFromService : step === 2 ? nextFromSpecialist : nextFromSlot}
                    >
                        <span>{t('booking.next')}</span>
                        <ChevronRightIcon />
                    </button>
                ) : (
                    // Never disabled on an incomplete form: pressing it is what asks the server
                    // what is wrong, and the answer marks the fields.
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                        {submitting ? <span className="btn-spinner" aria-hidden="true" /> : <CheckIcon />}
                        <span>{submitting ? t('booking.submitting') : t('booking.submit')}</span>
                    </button>
                )}
            </div>
        </>
    );

    return (
        <div className="book-page">
            <BookingHeader />

            <div className="page">
                {submitted === null && bookingOpen && <BookingStepper current={step} skipSlot={online} />}

                <div className="page-inner">
                    {submitted !== null ? (
                        <div className="step-panel">
                            <BookingSuccess
                                serviceName={serviceName}
                                specialistName={specialistName}
                                format={format}
                                date={online ? null : date}
                                time={online ? null : time}
                                slotHeld={submitted.slotHeld}
                            />
                        </div>
                    ) : !bookingOpen ? (
                        <div className="step-panel">
                            <div className="card">
                                <h1 className="card-title">{t('booking.title')}</h1>
                                <p className="desc-txt">{t('booking.closed')}</p>
                            </div>
                        </div>
                    ) : (
                        <form className="step-panel" onSubmit={(event) => void onSubmit(event)}>
                            {step === 1 && (
                                <ServiceStep
                                    types={shownTypes}
                                    loading={typesLoading}
                                    paid={paid}
                                    selected={selectedType}
                                    format={format}
                                    onlineOffered={onlineOffered}
                                    city={city}
                                    errors={fieldErrors}
                                    onTabChange={changeTab}
                                    onSelect={selectService}
                                    onFormatChange={changeFormat}
                                    onCityChange={selectCity}
                                >
                                    {actions}
                                </ServiceStep>
                            )}

                            {step === 2 && (
                                <SpecialistStep
                                    specialists={offeredSpecialists}
                                    loading={specialistsLoading}
                                    selected={selectedSpecialist?.id ?? null}
                                    errors={fieldErrors}
                                    onSelect={selectSpecialist}
                                >
                                    {actions}
                                </SpecialistStep>
                            )}

                            {step === SLOT_STEP && (
                                <SlotStep
                                    page={page}
                                    pages={pages}
                                    days={days}
                                    today={today}
                                    loading={calendarLoading}
                                    specialistChosen={selectedSpecialist !== null}
                                    date={date}
                                    time={time}
                                    takenTimes={takenTimes}
                                    errors={fieldErrors}
                                    onPageChange={setShownPage}
                                    onDateChange={(value) => {
                                        setParams({ date: value, time: null });
                                        reveal('times', { date: value, time: null });
                                    }}
                                    onTimeChange={(value) => {
                                        setParams({ time: value });
                                        reveal('actions', { time: value });
                                    }}
                                >
                                    {actions}
                                </SlotStep>
                            )}

                            {step === CONTACT_STEP && (
                                <ContactStep
                                    serviceName={serviceName}
                                    specialistName={specialistName}
                                    format={format}
                                    date={online ? null : date}
                                    time={online ? null : time}
                                    firstName={signedIn ? (profile?.firstName ?? '') : firstName}
                                    lastName={signedIn ? (profile?.lastName ?? '') : lastName}
                                    phone={signedIn ? toSubscriberDigits(profile?.phone ?? '') : phone}
                                    email={signedIn ? (profile?.email ?? '') : email}
                                    company={company}
                                    consent={consent}
                                    account={
                                        signedIn
                                            ? { loading: profile === undefined, onSignOut: () => void signOut() }
                                            : null
                                    }
                                    errors={fieldErrors}
                                    onChange={(field, value) => setContactField(field, value)}
                                    onConsentChange={(value) => {
                                        setConsent(value);

                                        if (value) {
                                            reveal('actions');
                                            setFieldErrors((current) => {
                                                const { consentVersion: _removed, ...rest } = current;
                                                return rest;
                                            });
                                        }
                                    }}
                                >
                                    {actions}
                                </ContactStep>
                            )}
                        </form>
                    )}

                    <BookingSideInfo />
                </div>
            </div>

            <BookingFooter />
        </div>
    );

    function setContactField(field: ContactField, value: string): void {
        if (field === 'firstName') setFirstName(value);
        else if (field === 'lastName') setLastName(value);
        else if (field === 'phone') setPhone(value);
        else if (field === 'email') setEmail(value);
        else setCompany(value);
    }
}

function clampStep(raw: string | null): number {
    const step = Number(raw ?? '1');

    return Number.isInteger(step) && step >= 1 && step <= CONTACT_STEP ? step : 1;
}

/**
 * Whether a service belongs under the "paid" tab.
 *
 * `price` is `null` for a free service.
 */
function isPaid(type: PublicConsultationType): boolean {
    return type.price !== null;
}

function blankToNull(value: string): string | null {
    return value.trim() === '' ? null : value.trim();
}
